import React, { useState, useEffect } from 'react';
import api from '../api';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';
import { toast } from 'sonner';
import { Download, FileText } from 'lucide-react';

const Reports = () => {
  const [reportType, setReportType] = useState('expenses');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [shops, setShops] = useState([]);
  const [beneficiaries, setBeneficiaries] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);

  useEffect(() => {
    fetchMetadata();
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    setStartDate(firstDay.toISOString().split('T')[0]);
    setEndDate(now.toISOString().split('T')[0]);
  }, []);

  const fetchMetadata = async () => {
    try {
      const [categoriesRes, shopsRes, beneficiariesRes, paymentMethodsRes] = await Promise.all([
        api.get('/categories'),
        api.get('/shops'),
        api.get('/beneficiaries'),
        api.get('/payment-methods'),
      ]);

      setCategories(categoriesRes.data);
      setShops(shopsRes.data);
      setBeneficiaries(beneficiariesRes.data);
      setPaymentMethods(paymentMethodsRes.data);
    } catch (error) {
      toast.error('Errore nel caricamento dei metadati');
    }
  };

  const handleGenerateReport = async () => {
    if (!startDate || !endDate) {
      toast.error('Seleziona entrambe le date');
      return;
    }

    setLoading(true);
    try {
      const response = await api.get('/reports/expenses', {
        params: {
          start_date: new Date(startDate).toISOString(),
          end_date: new Date(endDate + 'T23:59:59').toISOString(),
        },
      });

      setReportData(response.data);
      toast.success('Report generato con successo');
    } catch (error) {
      toast.error('Errore nella generazione del report');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPDF = async () => {
    if (!startDate || !endDate) {
      toast.error('Seleziona entrambe le date');
      return;
    }

    try {
      const response = await api.get('/reports/pdf', {
        params: {
          start_date: new Date(startDate).toISOString(),
          end_date: new Date(endDate + 'T23:59:59').toISOString(),
          report_type: reportType,
        },
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `report_${startDate}_${endDate}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('PDF scaricato con successo');
    } catch (error) {
      toast.error('Errore nel download del PDF');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR',
    }).format(amount);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    }).format(date);
  };

  const getAggregatedData = () => {
    const aggregated = {};
    let totalAmount = 0;

    reportData.forEach((expense) => {
      totalAmount += expense.amount;
      
      let key, name, color;
      
      switch (reportType) {
        case 'category':
          key = expense.category_id;
          name = expense.category_name;
          const cat = categories.find(c => c.id === key);
          color = cat?.color || '#999999';
          break;
        case 'shop':
          key = expense.shop_id;
          name = expense.shop_name;
          color = '#4ECDC4';
          break;
        case 'beneficiary':
          key = expense.beneficiary_id;
          name = expense.beneficiary_name;
          color = '#45B7D1';
          break;
        case 'payment':
          key = expense.payment_method_id;
          name = expense.payment_method_name;
          color = '#FFA07A';
          break;
        default:
          key = expense.category_id;
          name = expense.category_name;
          const defaultCat = categories.find(c => c.id === key);
          color = defaultCat?.color || '#999999';
      }

      if (!aggregated[key]) {
        aggregated[key] = { name, amount: 0, count: 0, color };
      }
      aggregated[key].amount += expense.amount;
      aggregated[key].count += 1;
    });

    return { data: Object.values(aggregated), total: totalAmount };
  };

  const { data: chartData, total } = reportData.length > 0 ? getAggregatedData() : { data: [], total: 0 };

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div>
        <h2 className="text-3xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope' }}>
          Report
        </h2>
        <p className="text-slate-600">Genera e analizza report dettagliati delle tue spese</p>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-slate-100 shadow-[0_2px_8px_rgba(0,0,0,0.04)] p-6">
        <h3 className="text-lg font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Manrope' }}>
          Filtri Report
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <Label htmlFor="report-type">Tipo Report</Label>
            <Select value={reportType} onValueChange={setReportType}>
              <SelectTrigger className="mt-2" data-testid="report-type-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="expenses">Spese</SelectItem>
                <SelectItem value="category">Per Categoria</SelectItem>
                <SelectItem value="shop">Per Negozio</SelectItem>
                <SelectItem value="beneficiary">Per Beneficiario</SelectItem>
                <SelectItem value="payment">Per Metodo Pagamento</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="start-date">Data Inizio</Label>
            <Input
              id="start-date"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="mt-2"
              data-testid="start-date-input"
            />
          </div>

          <div>
            <Label htmlFor="end-date">Data Fine</Label>
            <Input
              id="end-date"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="mt-2"
              data-testid="end-date-input"
            />
          </div>

          <div className="flex items-end">
            <Button
              onClick={handleGenerateReport}
              disabled={loading}
              className="w-full bg-[#D94E1F] hover:bg-[#B83E15] text-white rounded-full"
              data-testid="generate-report-button"
            >
              <FileText className="w-4 h-4 mr-2" />
              {loading ? 'Caricamento...' : 'Genera Report'}
            </Button>
          </div>
        </div>
      </div>

      {/* Results */}
      {reportData.length > 0 && (
        <>
          {/* Summary */}
          <div className="bg-white rounded-xl border border-slate-100 shadow-[0_2px_8px_rgba(0,0,0,0.04)] p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2" style={{ fontFamily: 'Manrope' }}>
                  Riepilogo
                </h3>
                <p className="text-3xl font-bold text-[#D94E1F]" style={{ fontFamily: 'Manrope' }}>
                  {formatCurrency(total)}
                </p>
                <p className="text-sm text-slate-500 mt-1">{reportData.length} transazioni</p>
              </div>
              <Button
                onClick={handleDownloadPDF}
                className="bg-[#D94E1F] hover:bg-[#B83E15] text-white rounded-full"
                data-testid="download-pdf-button"
              >
                <Download className="w-4 h-4 mr-2" />
                Scarica PDF
              </Button>
            </div>

            {/* Chart */}
            {chartData.length > 0 && (
              <div className="mt-6">
                <ResponsiveContainer width="100%" height={400}>
                  <PieChart>
                    <Pie
                      data={chartData}
                      dataKey="amount"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      outerRadius={120}
                      label={(entry) => `${entry.name}: ${formatCurrency(entry.amount)}`}
                    >
                      {chartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => formatCurrency(value)} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>

          {/* Data Table */}
          <div className="bg-white rounded-xl border border-slate-100 shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
            <div className="p-6 border-b border-slate-100">
              <h3 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Manrope' }}>
                Dettaglio Transazioni
              </h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="report-table">
                <thead className="border-b border-slate-100">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">Data</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">Importo</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">Categoria</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">Negozio</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">Beneficiario</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">Pagamento</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {reportData.map((expense) => (
                    <tr key={expense.id} className="hover:bg-slate-50 transition-colors">
                      <td className="px-6 py-4 text-sm text-slate-600">{formatDate(expense.date)}</td>
                      <td className="px-6 py-4 text-sm font-semibold text-slate-900">
                        {formatCurrency(expense.amount)}
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-600">{expense.category_name}</td>
                      <td className="px-6 py-4 text-sm text-slate-600">{expense.shop_name}</td>
                      <td className="px-6 py-4 text-sm text-slate-600">{expense.beneficiary_name}</td>
                      <td className="px-6 py-4 text-sm text-slate-600">{expense.payment_method_name}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {reportData.length === 0 && !loading && (
        <div className="bg-white rounded-xl border border-slate-100 shadow-[0_2px_8px_rgba(0,0,0,0.04)] p-12">
          <div className="text-center text-slate-400">
            <FileText className="w-16 h-16 mx-auto mb-4 opacity-50" />
            <p className="text-lg font-medium mb-2">Nessun Report Generato</p>
            <p className="text-sm">Seleziona le date e clicca su "Genera Report" per visualizzare i dati</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default Reports;
